import React, { useState, useEffect } from 'react';
import { Repository, FilterParams } from '../../types';
import ProjectCard from './ProjectCard';
import { useAppStore } from '../../store';
import SearchFilters from '../search/SearchFilters';

interface ProjectGridProps {
  projects: Repository[];
}

const ProjectGrid: React.FC<ProjectGridProps> = ({ projects }) => {
  const {
    totalCount,
    currentPage,
    totalPages,
    searchParams,
    setCurrentPage,
    searchLoading
  } = useAppStore();

  // 筛选状态
  const [filters, setFilters] = useState<FilterParams>({});
  const [filteredProjects, setFilteredProjects] = useState<Repository[]>(projects);

  // 应用筛选条件
  useEffect(() => {
    let filtered = [...projects];

    // 描述语言筛选
    if (filters.description_language && filters.description_language !== 'all') {
      filtered = filtered.filter(project => {
        const description = project.description || '';
        const isChinese = /[\u4e00-\u9fa5]/.test(description);

        if (filters.description_language === 'chinese') {
          return isChinese;
        } else if (filters.description_language === 'english') {
          return !isChinese && description.length > 0;
        }
        return true;
      });
    }

    // 星数筛选
    if (filters.min_stars_filter !== undefined) {
      filtered = filtered.filter(project =>
        (project.stargazers_count || project.stars || 0) >=
        (filters.min_stars_filter || 0)
      );
    }

    if (filters.max_stars_filter !== undefined) {
      filtered = filtered.filter(project =>
        (project.stargazers_count || project.stars || 0) <=
        (filters.max_stars_filter || Infinity)
      );
    }

    // Fork 数筛选
    if (filters.min_forks_filter !== undefined) {
      filtered = filtered.filter(project =>
        (project.forks_count || project.forks || 0) >=
        (filters.min_forks_filter || 0)
      );
    }

    // 仓库大小筛选
    if (filters.size_range && filters.size_range !== 'all') {
      filtered = filtered.filter(project => {
        const sizeKB = project.size || 0;
        const sizeMB = sizeKB / 1024;

        switch (filters.size_range) {
          case 'small':
            return sizeMB < 1;
          case 'medium':
            return sizeMB >= 1 && sizeMB <= 10;
          case 'large':
            return sizeMB > 10;
          default:
            return true;
        }
      });
    }

    // 布尔条件筛选
    if (filters.has_topics) {
      filtered = filtered.filter(
        project => project.topics && project.topics.length > 0
      );
    }

    if (filters.has_wiki) {
      filtered = filtered.filter(project => project.has_wiki);
    }

    if (filters.has_pages) {
      filtered = filtered.filter(project => project.has_pages);
    }

    if (filters.is_archived === false) {
      filtered = filtered.filter(project => !project.archived);
    }

    setFilteredProjects(filtered);
  }, [projects, filters]);

  // 处理筛选条件变化
  const handleFiltersChange = (newFilters: FilterParams) => {
    setFilters(newFilters);
  };

  if (projects.length === 0) {
    return (
      <div className="container">
        <div className="text-center py-5">
          <i
            className="fas fa-search text-muted mb-3"
            style={{ fontSize: '3rem' }}
          ></i>
          <h4 className="text-muted">暂无搜索结果</h4>
          <p className="text-muted">请尝试调整搜索条件或关键词</p>
        </div>
      </div>
    );
  }

  // 分页处理
  const handlePageChange = async (page: number) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      await setCurrentPage(page);
    }
  };

  return (
    <section className="projects-section">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="section-header-with-filters mb-4">
              <div className="section-header-content">
                <h3>
                  <i className="fas fa-code-branch me-2"></i>
                  搜索结果 ({totalCount.toLocaleString()} 个项目)
                </h3>
                <p className="text-muted mb-0">
                  第 {currentPage} 页，共 {totalPages} 页 | 原始结果{' '}
                  {projects.length} 个，筛选后 {filteredProjects.length} 个项目
                </p>
              </div>

              {/* 右上角的紧凑筛选组件 */}
              <div className="section-header-filters">
                <SearchFilters
                  onFiltersChange={handleFiltersChange}
                  initialFilters={filters}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 筛选后的项目展示 */}
        {filteredProjects.length > 0 ? (
          <div className="projects-grid">
            {filteredProjects.map((project, index) => (
              <div
                key={`${project.full_name}-${index}`}
                className="project-grid-item"
              >
                <ProjectCard project={project} />
              </div>
            ))}
          </div>
        ) : (
          <div className="no-filtered-results">
            <div className="text-center py-5">
              <i
                className="fas fa-filter text-muted mb-3"
                style={{ fontSize: '3rem' }}
              ></i>
              <h4 className="text-muted">筛选后无结果</h4>
              <p className="text-muted">
                当前筛选条件下没有匹配的项目，请尝试调整筛选条件
              </p>
              <button
                className="btn btn-outline-primary mt-3"
                onClick={() => setFilters({})}
              >
                <i className="fas fa-undo me-2"></i>
                重置筛选条件
              </button>
            </div>
          </div>
        )}

        {/* 分页组件 */}
        {totalPages > 1 && (
          <div className="row mt-4">
            <div className="col-12">
              <nav aria-label="搜索结果分页">
                <ul className="pagination justify-content-center">
                  {/* 上一页 */}
                  <li
                    className={`page-item ${
                      currentPage === 1 || searchLoading ? 'disabled' : ''
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || searchLoading}
                    >
                      {searchLoading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> 加载中
                        </>
                      ) : (
                        <>
                          <i className="fas fa-chevron-left"></i> 上一页
                        </>
                      )}
                    </button>
                  </li>

                  {/* 页码 */}
                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(
                      1,
                      currentPage - Math.floor(maxVisiblePages / 2)
                    );
                    let endPage = Math.min(
                      totalPages,
                      startPage + maxVisiblePages - 1
                    );

                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(
                        1,
                        endPage - maxVisiblePages + 1
                      );
                    }

                    // 第一页
                    if (startPage > 1) {
                      pages.push(
                        <li
                          key={1}
                          className={`page-item ${
                            searchLoading ? 'disabled' : ''
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(1)}
                            disabled={searchLoading}
                          >
                            1
                          </button>
                        </li>
                      );
                      if (startPage > 2) {
                        pages.push(
                          <li
                            key="start-ellipsis"
                            className="page-item disabled"
                          >
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                    }

                    // 中间页码
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <li
                          key={i}
                          className={`page-item ${
                            i === currentPage ? 'active' : ''
                          } ${searchLoading ? 'disabled' : ''}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(i)}
                            disabled={searchLoading}
                          >
                            {searchLoading && i === currentPage ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              i
                            )}
                          </button>
                        </li>
                      );
                    }

                    // 最后一页
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <li
                            key="end-ellipsis"
                            className="page-item disabled"
                          >
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                      pages.push(
                        <li
                          key={totalPages}
                          className={`page-item ${
                            searchLoading ? 'disabled' : ''
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={searchLoading}
                          >
                            {totalPages}
                          </button>
                        </li>
                      );
                    }

                    return pages;
                  })()}

                  {/* 下一页 */}
                  <li
                    className={`page-item ${
                      currentPage === totalPages || searchLoading
                        ? 'disabled'
                        : ''
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || searchLoading}
                    >
                      {searchLoading ? (
                        <>
                          加载中 <i className="fas fa-spinner fa-spin"></i>
                        </>
                      ) : (
                        <>
                          下一页 <i className="fas fa-chevron-right"></i>
                        </>
                      )}
                    </button>
                  </li>
                </ul>
              </nav>

              {/* 分页信息 */}
              <div className="text-center mt-3">
                <small className="text-muted">
                  显示第{' '}
                  {((currentPage - 1) * (searchParams?.per_page || 60)) + 1} -
                  {Math.min(
                    currentPage * (searchParams?.per_page || 60),
                    totalCount
                  )}{' '}
                  项，共 {totalCount.toLocaleString()} 项
                </small>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectGrid;

